<template>
  <div class="hourlist-table-wrap">
    <div v-if="$store.getters['filterStore/getFilter'].activeView !== 'week' && $store.getters['filterStore/getFilter'].activeView !== 'report' && showTopActions" class="d-flex justify-content-between mb-2">
      <div class="d-flex align-items-end">
        <div class="tablefilters-info">
          <div>{{ $gettext('Showing hours results for') }} {{ tableDate }}</div>
          <div v-if="focusDate" class="info-btn" role="button" @click="$emit('unfocus-day', false)">
            {{ focusFormatedDate }} 
            <font-awesome-icon aria-hidden="true" icon="fa-solid fa-circle-xmark" />
          </div>
        </div>
      </div>
      <div class="d-flex justify-content-end">
        <GekkoButton
          @click="$emit('new-event', false)"
          :text="$gettext('Add hour')"
          icon="fa-solid fa-plus"
          size="sm"
        />
      </div>
    </div>

    <div v-if="$store.getters['filterStore/getFilter'].activeView === 'report'" class="table-action-buttons">
      
      <div v-if="hourList.length" class="select-all-container">
        <div @click="selectAllTimeentries()" :style="selectAllTimeentriesStyle()" class="select-icon-container select-all-icon-container">
          <font-awesome-icon v-if="showSelectAllCheck()" icon="fa-solid fa-check" class="icon-color" />
          <div v-if="!showSelectAllCheck()" class="empty-check"></div>
        </div>
        <div class="select-all-text" @click="selectAllTimeentries()" v-translate>Select all</div>
      </div>

      <GekkoButton 
        v-if="hourList.length"
        :disabled="!selectedTimeentries.length"
        @click="markHoursAsInvoiced()"
        :text="$gettext('Mark as invoiced')"
        icon="fa-solid fa-file-lines"
        variant="link"
        size="sm"
      />
      <GekkoButton
        v-if="hourList.length"
        @click="$emit('new-event', false)"
        :text="$gettext('Add hour')"
        icon="fa-solid fa-plus"
        size="sm"
      />
    </div>

    <div class="content--inner">
      <b-table
        :items="hourListRender"
        :class="['gk-table gk-table--responsive gk-table--striped gk-table--striped gk-table--hover', { loading: loading, 'gk-table--headed': selectableRows }]"
        :fields="tableFields"
        :tbody-tr-class="rowClass"
        @row-clicked="rowClicked"
        :busy="loading"
        show-empty
        table-busy
      >
        <template #table-busy="">
          <LoadingSpinner min-height />
        </template>
        <template #empty="">
          <EmptyState :class="{ 'emptystate-simple': componentOnModal }">
            <template v-if="!componentOnModal" v-slot:image>
              <img src="/static/img/empty-states/empty-state-cost.png">
            </template>
            <template v-slot:title>
              {{ $gettext('We couldn\'t find any hours') }}
            </template>
            <template v-slot:content>
              {{ $gettext('Start by creating a new one or try updating your filters.') }}
            </template>
            <template v-if="!componentOnModal" v-slot:cta>
                <GekkoButton
                  @click="$emit('new-event', false)"
                  :text="$gettext('Add a new hour')"
                  icon="fa-solid fa-plus"
                />
            </template>
          </EmptyState>
        </template>
        
        <template v-slot:head(selector)="">
          <b-form-checkbox v-model="allItemsSelected" @change="selectAllTableItems" />
        </template>

        <template v-if="selectableRows" #cell(selector)="row">
          <b-form-checkbox v-model="row.item.selected" @change="selectedTableItem" />
        </template>

        <template #cell(select_hour)="data">
          <div @click.stop class="select-icon-container-wrap">
            <div @click="timeentrySelected(data)" @stop="rowClicked" class="select-icon-container">
              <font-awesome-icon v-if="showCheck(data)" icon="fa-solid fa-check" class="icon-color" />
              <div v-if="!showCheck(data)" class="empty-check"></div>
            </div>
          </div>
        </template>

        <template #cell(day)="data">
          <div class="coldate">
            {{ new Date(data.item.date).format('d M Y') }}
          </div>
        </template>
        <template #cell(project)="data">
          <div class="project-name">
            <span style="font-weight: 900">{{ projectContactLabel(data.item).projectName }}</span>
            &nbsp;
            <span>{{ projectContactLabel(data.item).companyName }}</span>
            <br><small class="coldesc description">{{ data.item.description }} </small>
          </div>
        </template>
        <template #cell(date)="data">
          {{ new Date(data.value).format('H:i') }}
          <span class="table-date-join">{{ $gettext('to')}}</span>
          {{ getEndTime(data.item) }}
        </template>
        <template #cell(end)="data">
          {{ getEndTime(data.item) }}
        </template>
        <template #cell(amount)="data">
          {{ data.value | amount2HourMin($gettext("h"), $gettext("m")) }}
        </template>
        <template #cell(hour_rate)="data">
          <div 
            v-if="getHourRate(data.item)" 
            v-b-tooltip="{ trigger: 'hover', delay: { show: 500 } }" 
            :title="$gettext('Estimated revenue based on hourly rate.')" 
            class="text-right">
              {{ getHourRate(data.item) | currencyString('EUR') }}
          </div>
        </template>
        <template #cell(is_invoiced)="data">
          <div class="d-flex justify-content-end">
              <span class="status-badge" :class="['status-' + getStatusClass(data.item)]">
                {{ getItemStatus(data.item) }}
              </span>
          </div>
        </template>
        <template #cell(created_by)="data">
          <div class="d-flex"><Avatar :key="data.value.id" :userId="data.value.id" /></div>
        </template>
        <template #cell(action)="data">
          <div class="text-right">
            <b-dropdown class="action-dropdown ui-dashboard-menu-dropdown" no-caret>
              <template #button-content>
                <font-awesome-icon icon="fa-solid fa-ellipsis-vertical" style="margin: 0;" />
              </template>
              <b-dropdown-item @click="copyHour(data.item)"><font-awesome-icon icon="fa-regular fa-copy" /> <translate>Copy</translate></b-dropdown-item>
              <b-dropdown-item @click="deleteEvent(data.item)"><font-awesome-icon icon="fa-regular fa-copy" /> <translate>Delete</translate></b-dropdown-item>
            </b-dropdown>
          </div>
        </template>
        <template slot="bottom-row" v-if="nextUrl">
            <td :colspan="fields.length" class="table-load-more">
              <GekkoButton 
                v-if="nextUrl && !fetchingData" 
                @click="handleScroll()"
                id="load_more_button" 
                class="text-info"
                variant="link" 
                :text="$gettext('Load more hours')" 
              />
              <LoadingSpinner v-if="fetchingData" />
            </td>
          </template>
      </b-table>
    </div>
  </div>
</template>
<script>
  import { mapGetters } from 'vuex';

  import moment from 'moment';
  import HourService from "@/store/services/hour.service.js";
  import Avatar from "@/components/Avatar";
  import EmptyState from "@/components/ui/EmptyState"
  import GekkoButton from "@/components/GekkoButton";
  import LoadingSpinner from "@/components/dashboard/ui/LoadingSpinner"

  export default {
    name: "HourListTable",
    components: { Avatar, EmptyState, GekkoButton, LoadingSpinner },
    props: {
      value: String,
      hourList: {
        type: Array,
        default: () => []
      },
      focusDate: {
        type: String,
        default: null
      },
      selectableRows: {
        type: Boolean,
        default: false
      },
      componentOnModal: {
        type: Boolean,
        default: false
      },
      excludeTableFields: {
        type: Array,
        default: () => []
      },
      showTopActions: {
        type: Boolean,
        default: true
      },
      listAll: {
        type: Boolean,
        default: false
      },
    },
    data () {
      return {
        eventCreationState: null,
        selectedEvent: {},
        hourListRender: [],
        loading: true,
        fetchingData: true,
        nextUrl: null,
        fields: [
          {key:'day', label: this.$gettext("Date"), class: 'tablecol-md'},
          {key:'project', label: this.$gettext("Project")},
          {key:'date', label: this.$gettext("Start time"), class: 'tablecol-sm'},
          {key:'amount', label: this.$gettext("Duration"), class: 'tablecol-sm'},
          {key:'is_invoiced', label: this.$gettext("Status"), class: 'tablecol-xs'},
          {key:'created_by', label: this.$gettext("User"), class: 'tablecol-xs'},
          {key:'action', label: '', class: 'tablecol-xs'}
        ],
        selectedTimeentries: [],
        allItemsSelected: false
      }
    },
    computed: {
      ...mapGetters({ 
        getCompanyId: 'main/getCompanyId',
        currentCompany: 'main/getCurrentCompany'
      }),
      tableDate () {
        return moment(this.$store.getters['filterStore/getFilter'].start_date).format("MMMM YYYY")
      },
      focusFormatedDate () {
        return moment(this.focusDate).format("dddd Do")
      },
      selectedTableItems () {
        let selected = null
        if (this.hourListRender) {
          selected =  this.hourListRender.filter(item => item.selected)
        }
        return selected
      },
      tableFields () {
        return this.fields.filter(field => !this.excludeTableFields.includes(field.key));
      }
    },
    watch: {
      hourList (val) {
        this.hourListRender = val
        this.loading = false
        this.fetchingData = false
      }
    },
    created () {
      window.addEventListener('scroll', this.handleScroll);
      if (this.selectableRows) {
        this.fields.unshift({ key: 'selector', label: '' })
      }
      if (this.$store.getters['filterStore/getFilter'].activeView === 'report') {
        this.fields.unshift({key:'select_hour', label:'', class: 'tablecol-xs'});
        this.fields.splice(5, 0, {key:'hour_rate', label: this.$gettext("Hour Rate"), class: 'tablecol-xs'});
      }
      if (this.componentOnModal) {
        this.getHourList()
      }
    },
    destroyed () {
      window.removeEventListener('scroll', this.handleScroll);
      if (this.componentOnModal) {
        this.$emit('destroyed')
      }
    },
    methods: {
      rowClass (item) {
        if(item && item.selected) return 'item-selected'
      },
      getStatusClass (item) {
        return !item.project ? 'unassigned' : item.is_invoiced ? 'complete' : 'incomplete'
      },
      getItemStatus (item) {
        return !item.project ? this.$gettext('Unassigned') : item.is_invoiced ? this.$gettext('Invoiced') : this.$gettext('Uninvoiced')
      },
      getHourList (date_range = false) {
        this.loading = true
        this.fetchingData = true
        var filter = { ...this.$store.getters["filterStore/getFilter"] }
        filter.start_date = (date_range.start_date) ? new Date(date_range.start_date).format("d-m-Y") : filter.start_date
        filter.end_date = (date_range.end_date) ? 
          new Date(date_range.end_date).format("d-m-Y") 
          : (date_range.start_date) ? filter.start_date : filter.end_date 

        filter.extended = "True"; // to get avatar data
        if(this.$store.getters["filterStore/getCurrentProject"]){
          filter.project = this.$store.getters["filterStore/getCurrentProject"];
        }else if(this.$store.getters["filterStore/getCurrentCustomer"]){
          filter.project__company = this.$store.getters["filterStore/getCurrentCustomer"];
        }

        if(this.$store.getters["filterStore/getFilter"].created_by) {
          filter.created_by = this.$store.getters["filterStore/getFilter"].created_by;
        }

        filter.company_profile = this.getCompanyId;
        
        const listHoursApi = (this.listAll) ? 'listAll' : 'list'
        HourService[listHoursApi](filter).then((response) => {
          this.hourListRender = []

          if (!this.listAll) {
            this.nextUrl = response.data.next;
          }

          const hours_results = (this.listAll) ? response.data : response.data.results

          for (var i = 0; i < hours_results.length; i++) {
            if (this.$store.getters["filterStore/getUnassigned"] || filter.status === 'unassigned') {
              if (!hours_results[i].project) { // only unassigned
                let event = HourService.timeentry2Event(hours_results[i])
                this.hourListRender.push(event)
              }
            } else {
              if (filter.status && filter.status !== 'all') {  // only assigned
                if (hours_results[i].project) {
                  let event = HourService.timeentry2Event(hours_results[i])
                  this.hourListRender.push(event)
                }
              } else {
                let event = HourService.timeentry2Event(hours_results[i])
                this.hourListRender.push(event)
              }
            }
          }
          if (this.selectableRows) {
            if (this.listAll) {
              this.$emit('get-hours', hours_results.length)
            } else {
              this.$emit('get-hours', response.data.count)
            }
          }
          this.loading = false
          this.fetchingData = false
        })
      },
      rowClicked (item) {
        if (!this.componentOnModal) {

          this.eventCreationState = 'edit';
          this.selectedEvent = HourService.timeentry2Event(item)

          if(!item.amount_hours && !item.amount_minutes) {
            this.selectedEvent.start = item.start
            this.selectedEvent.end = item.end
          }

          // also need date objects for start and end (week view this is handled by vue-cal)
          // replace added for safari
          if(this.selectedEvent.start && this.selectedEvent.end) {
            this.selectedEvent.start = new Date(this.selectedEvent.start.replace(/-/g, "/"))
            this.selectedEvent.end = new Date(this.selectedEvent.end.replace(/-/g, "/"))
          }

          this.$emit('row-clicked', this.selectedEvent);
          
        } else {
          if (this.selectableRows) {
            const event_index = this.hourListRender.findIndex(event => event.id === item.id)
            this.$set(this.hourListRender, event_index, { ...this.hourListRender[event_index], selected: !item.selected })
            this.selectedTableItem()
          } else {
            this.$emit('row-clicked', item)
          }
        }
      },
      selectAllTableItems () {
        this.hourListRender.forEach((event, event_index) => {
          this.$set(this.hourListRender, event_index, { ...event, selected: this.allItemsSelected })
        });
        this.selectedTableItem()
      },
      selectedTableItem () {
        this.hourListRender.splice() // ugly hack for triggering computed property update
        this.$emit('selected-item', this.selectedTableItems)
      },
      getEndTime (data) {
        const time = new Date(data.date)
        const hours = (data.amount_hours) ? data.amount_hours : Math.floor(data.amount)
        const minutes = (data.amount_minutes) ? data.amount_minutes : (data.amount - hours) * 60;
        time.setHours( time.getHours() + hours );
        time.setMinutes( time.getMinutes() + minutes );
        return time.format('H:i')
      },
      deleteEvent (item) {
        if (item.recurrent_time_entry.length)
          this.$emit("delete-recurrent-event", item);
        else
          this.$emit("delete-event", item);
      },
      copyHour(item) {
        this.$emit("copyHour", JSON.parse(JSON.stringify(item)));
      },
      isInViewport (element_id) {
        var element = document.getElementById(element_id)
        var rect = element.getBoundingClientRect()
        var html = document.documentElement
        return (
          rect.top >= 0 &&
          rect.left >= 0 &&
          rect.bottom <= (window.innerHeight || html.clientHeight) &&
          rect.right <= (window.innerWidth || html.clientWidth)
        )
      },
      handleScroll () {
        if (!this.fetchingData && this.nextUrl) {
          var loadNewHours = this.isInViewport('load_more_button');
          if (loadNewHours) {
            this.fetchingData = true
            HourService.loadNextPage(this.nextUrl).then((response) => {
              this.fetchingData = false
              this.nextUrl = response.data.next

              var filter = { ...this.$store.getters["filterStore/getFilter"] }

              for (var i = 0; i < response.data.results.length; i++) {
                if (this.$store.getters["filterStore/getUnassigned"] || filter.status === 'unassigned') {
                  if (!response.data.results[i].project) { // only unassigned
                    let event = HourService.timeentry2Event(response.data.results[i])
                    this.hourListRender.push(event)
                  }
                } else {
                  if (filter.status && filter.status !== 'all') {  // only assigned
                    if (response.data.results[i].project) {
                      let event = HourService.timeentry2Event(response.data.results[i])
                      this.hourListRender.push(event)
                    }
                  } else {
                    let event = HourService.timeentry2Event(response.data.results[i])
                    this.hourListRender.push(event)
                  }
                }
              }

              if (this.selectableRows) this.$emit('get-hours', response.data.count)
            })
          }
        }
      },
      timeentrySelected(row) {
        if (this.selectedTimeentries.includes(row.item.id)) {
          const index = this.selectedTimeentries.indexOf(row.item.id);
          if (index > -1)
            this.selectedTimeentries.splice(index, 1);
        } else
          this.selectedTimeentries.push(row.item.id);
      },
      showCheck(row) {
        return this.selectedTimeentries.includes(row.item.id);
      },
      markHoursAsInvoiced() {
        let uninvoicedHour = this.hourList.find(hour => this.selectedTimeentries.includes(hour.id) && !hour.project);
        if (uninvoicedHour) {
          this.$store.commit('setAlert', {
            title: this.$gettext('Warning!'),
            text: this.$gettext('Some of the hours you selected are unassigned. You need to assign these first before you can mark them as invoiced.'),
            variant: "warning",
            duration: 4 
          });
        }

        HourService.markAsInvoiced(this.selectedTimeentries).then((response) => {
          this.$emit("updated", this.selectedTimeentries);
          this.selectedTimeentries = [];
          
          this.$store.commit('setAlert', { 
            title: this.$gettext('Success'),
            text: this.$gettext('Hours marked as invoiced.'),
            variant: "success",
            duration: 4 
          });
        }).catch(() => {
          this.$store.commit('setAlert', { 
              title: this.$gettext('Error'),
              text: this.$gettext('Some hours could not get marked as invoiced.'),
              variant: "danger",
              duration: 4 
          });
        })
      },
      showSelectAllCheck() {
        return this.selectedTimeentries.length === this.hourList.length;
      },
      selectAllTimeentries() {
        if (this.selectedTimeentries.length === this.hourList.length)
          this.selectedTimeentries = [];
        else
          this.selectedTimeentries = this.hourList.map(hour => hour.id);
      },
      selectAllTimeentriesStyle() {
        return "color: #b9b4b4";
      },
      projectContactLabel(item) {
        let projectName = ''
        let companyName = ''

        if (item.project) {
          projectName = item.project.name

          if (item.project.company) companyName = item.project.company.name
        }
        
        return {
          projectName: projectName,
          companyName: companyName
        }
      },
      getHourRate(hour) {
        if (hour.project) {
          if (hour.project.company.id === this.currentCompany.id) return null
          else if (hour.project.hourly_rate) return hour.project.hourly_rate * hour.amount
          else if (hour.project.company.hourly_rate) return hour.project.company.hourly_rate * hour.amount
        }
        if (this.currentCompany.hourly_rate) return this.currentCompany.hourly_rate * hour.amount
        return null
      }
    }
  }
</script>

<style lang="scss">
.action-dropdown {
  .btn-secondary {
    background-color: transparent !important;
    border-color: transparent !important;
    color: $colorBlue !important;
  }

  .btn-secondary:hover {
    color: $colorBlue800 !important;
  }
}
.table-date-join {
  font-size: 14px;
  color: $colorUnassigned;
  padding: 0px 2px;
}
.tablefilters-info {
  font-size: 16px; color: #a7a7a7; 
  display: flex;
  .info-btn {
    font-size: 14px;
    color: $colorBlue;
    padding: 0 10px;
    line-height: 24px;
    margin-left: 10px;
    border-radius: 20px;
    background: #091e420a;
  }
}

.table-action-buttons {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-bottom: 1rem;
}
.select-icon-container-wrap {
  display: flex;
  justify-content: center;
}
.select-icon-container {
  border: solid $colorBrand 0.15rem;
  border-radius: 0.3rem;
  display: flex;
  width: fit-content;
}

.select-all-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  flex: 1;
  padding-left: 26px;
  cursor: pointer;
  font-size: 1rem;
}

.select-all-text {
  color: $colorBrand;
  margin-left: 0.5rem;
}

.icon-color {
  color: $colorBrand;
}

.empty-check {
  min-width: 1rem;
  min-height: 1rem;
}
</style>

<style lang="scss" scoped>
  // .hourlist-table-wrap { width: 100%; overflow-x: scroll; }
  // @media(max-width: '767px') {
  //   .hourlist-table-wrap { overflow-x: scroll; }
  // }
  .hourlist-table-wrap {
    .content--inner {
      min-height: 360px;
      overflow: initial;
    }
  }
</style>
